import React from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Alert,
} from "@mui/material";
import { useAccessibleCircles } from "../../../hooks/useAccessibleCircles";

interface SubcircleSelectionStepProps {
  formData: {
    title: string;
    body: string;
    type: string;
    audience: string;
    attachmentUrl: string;
    uploadedFiles?: Array<{
      fileUrl: string;
      fileName: string;
      contentType: string;
      size: number;
    }>;
    eventStartDate?: Date | null;
    eventStartTime?: string;
    eventEndDate?: Date | null;
    eventEndTime?: string;
    eventLocation?: string;
    imageUrl?: string;
    eventBackgroundImage?: string;
    targetSubcircle?: string;
  };
  errors: {
    title?: string;
    body?: string;
    type?: string;
    audience?: string;
    attachmentUrl?: string;
    eventStartDate?: string;
    eventStartTime?: string;
    eventEndDate?: string;
    eventEndTime?: string;
    eventLocation?: string;
    targetSubcircle?: string;
  };
  handleChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value: any } }
  ) => void;
}

const SubcircleSelectionStep: React.FC<SubcircleSelectionStepProps> = ({
  formData,
  errors,
  handleChange,
}) => {
  const { subcircles, isLoading: loading, error } = useAccessibleCircles();

  const handleSubcircleChange = (subcircleId: string) => {
    handleChange({
      target: {
        name: "targetSubcircle",
        value: subcircleId,
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6">Loading subcircles...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load subcircles. Please try again.</Alert>
      </Box>
    );
  }

  if (subcircles.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No subcircles found. You need to create subcircles first before
          sending targeted notices.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Select Target Subcircle
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Choose which subcircle should receive this notice. Only members of the
        selected subcircle will receive the notification.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth error={!!errors.targetSubcircle}>
          <InputLabel id="subcircle-select-label">Subcircle</InputLabel>
          <Select
            labelId="subcircle-select-label"
            id="subcircle-select"
            value={formData.targetSubcircle || ""}
            label="Subcircle"
            onChange={(e) => handleSubcircleChange(e.target.value)}
          >
            {subcircles.map((subcircle) => (
              <MenuItem key={subcircle.circleId} value={subcircle.circleId}>
                {subcircle.name}
                {subcircle.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    - {subcircle.description}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
          {errors.targetSubcircle && (
            <FormHelperText>{errors.targetSubcircle}</FormHelperText>
          )}
        </FormControl>
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> This notice will only be sent to members of the
          selected subcircle. If you want to send to everyone in your school,
          use a regular info notice instead.
        </Typography>
      </Alert>
    </Box>
  );
};

export default SubcircleSelectionStep;
